import React, { useEffect, useState } from 'react';
import Banner from '../assets/Banner.png';
import User from '../assets/user.png';
import { useNavigate, useParams } from 'react-router-dom';
import {
  FaBuilding,
  FaEnvelope,
  FaGlobe,
  FaLink,
  FaPhoneAlt,
  FaRegFilePdf,
  FaUsers,
  FaWhatsapp,
} from 'react-icons/fa';
import { FaFacebookF, FaInstagram, FaLinkedinIn, FaXTwitter } from 'react-icons/fa6';
import { SlHandbag, SlMap } from 'react-icons/sl';
import api from '../services/Api';
import { IoIosArrowRoundDown, IoIosArrowRoundUp } from 'react-icons/io';
import toast from 'react-hot-toast';
import { showError, showLoading, showSuccess } from '../components/Ui/ToastNotification';

const CompanyDetailsPage = () => {
  const { companyId } = useParams();
  const navigate = useNavigate();
  const [businessDetails, setBusinessDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const transactions = [
    // Your transactions data...
  ];

  const socialIcons = {
    linkedin: <FaLinkedinIn />,
    facebook: <FaFacebookF />,
    whatsapp: <FaWhatsapp />,
    twitter: <FaXTwitter />,
    instagram: <FaInstagram />,
  };

  useEffect(() => {
    if (!companyId) {
      console.error('No companyId found in URL parameters');
      return;
    }

    const loadingToast = showLoading('Fetching business details...'); // Show loading toast

    setLoading(true);
    api
      .get(`/admin/business/${companyId}`)
      .then(response => {
        console.log('API Response:', response.data);
        if (response.data.business) {
          setBusinessDetails(response.data.business);
          toast.dismiss(loadingToast); // Dismiss loading toast
          showSuccess('Business details loaded successfully!'); // Show success toast
        } else {
          throw new Error('No business data');
        }
      })
      .catch(error => {
        console.error('Error:', error);
        toast.dismiss(loadingToast); // Dismiss loading toast
        showError('Failed to fetch business details. Please try again.'); // Show error toast
        setError(error);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [companyId]);

  const shareProfile = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      showSuccess('Profile link copied to clipboard!'); // Show success toast
    } catch (err) {
      console.error('Failed to copy:', err);
      showError('Failed to copy profile link. Please try again.'); // Show error toast
    }
  };

  const handleDownloadPdf = async document => {
    try {
      const frontendUrl = window.location.origin;
      const documentUrl = `${frontendUrl}/${document.fileName}`;
      window.open(documentUrl, '_blank');
    } catch (error) {
      console.error('Error viewing document:', error);
      showError('Failed to open the document. Please try again later.'); // Show error toast
    }
  };

  const handlePaymentDownload = transaction => {
    navigate('/receipt', {
      state: {
        transaction,
        headers: [
          { key: 'description', label: 'Description' },
          { key: 'id', label: 'Transaction ID' },
          { key: 'type', label: 'Type' },
          { key: 'card', label: 'Card' },
          { key: 'date', label: 'Date' },
          { key: 'amount', label: 'Amount' },
        ],
      },
    });
  };

  const handleDownload = transaction => {
    navigate('/receipt', {
      state: {
        transaction,
        headers: [
          { key: 'company', label: 'Company' },
          { key: 'id', label: 'ID' },
          { key: 'date', label: 'Date' },
          { key: 'phone', label: 'Phone number' },
          { key: 'quantity', label: 'Quantity(pcs)' },
        ],
      },
    });
  };

  if (loading) {
    return (
      <div className="ml-64 flex-grow w-[80%] min-h-[calc(100vh-63px)] bg-[#F4F5F6] flex justify-center items-center">
        <div className="w-10 h-10 border-4 border-gray-300 border-t-[#000] rounded-full animate-spin"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="ml-64 flex-grow w-[80%] min-h-[calc(100vh-63px)] bg-[#F4F5F6] flex justify-center items-center">
        <div className="text-center text-red-500">
          <p className="mb-2 text-xl font-semibold">Error Loading Business Details</p>
          <p>{error.message}</p>
        </div>
      </div>
    );
  }

  if (!businessDetails) {
    return (
      <div className="ml-64 flex-grow w-[80%] min-h-[calc(100vh-63px)] bg-[#F4F5F6] flex justify-center items-center">
        <div className="text-gray-500">No business details found</div>
      </div>
    );
  }
  return (
    <div className="ml-64 flex-grow w-[80%] min-h-[calc(100vh-63px)] bg-[#F4F5F6] font-nohemi">
      <div className="container">
        <div className="p-3">
          <div className="relative w-full mx-auto mt-2 bg-white rounded-lg h-44">
            <div className="relative w-full overflow-hidden rounded-t-lg h-2/3">
              <img className="object-cover w-full h-full" src={Banner} alt="alt" />
            </div>
            <div className="absolute bottom-0 flex items-end left-6">
              <div className="pb-6">
                <img
                  className="w-32 h-32 border-4 border-white border-solid rounded-full"
                  src={User}
                  alt="user"
                />
              </div>
              {businessDetails?.representatives?.map((rep, index) => (
                <div key={rep._id} className="flex flex-col justify-end mb-3 ml-4">
                  <p className="font-semibold text-sm text-[#191B1C]">
                    {`${rep.firstName} ${rep.lastName}`}
                  </p>
                  <p className="font-light text-[10px] text-[#7B878C]">{rep.roleInOrganization}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="w-full py-4 mx-auto mt-4 bg-white rounded-lg h-28 px-7">
            <h2 className="font-semibold text-sm text-[#191B1C] pt-2">{businessDetails.name}</h2>
            <h3 className="font-light text-[10px] text-[#7B878C]">
              {businessDetails?.serviceType?.name}
            </h3>
            <div className="flex flex-row items-center justify-start">
              <p className="font-light text-[10px] text-[#7B878C] pr-2">
                {businessDetails.location}
              </p>
              <p className="font-light text-[10px] text-[#7B878C] pr-2">
                . {businessDetails?.number_of_project_per_year}
              </p>
              <p className="font-light text-[10px] text-[#7B878C] pr-2">
                . {businessDetails?.team_Size} employees
              </p>
            </div>
          </div>

          <div className="w-full py-4 mx-auto mt-4 bg-white rounded-lg h-28 px-7">
            <h2 className="font-semibold text-sm text-[#191B1C] py-2">About</h2>
            <h3 className="font-light text-[10px] text-[#7B878C] line-clamp-3">
              {businessDetails?.bio}
            </h3>
          </div>

          <div className="flex flex-col w-full py-4 mx-auto mt-4 bg-white rounded-lg px-7">
            <div className="flex flex-col gap-6 md:flex-row">
              <div className="w-full space-y-6 md:w-1/2">
                <h2 className="font-semibold text-sm text-[#191B1C]">Description</h2>
                <p className="font-light text-[10px] text-[#7B878C]">
                  {businessDetails?.company_description}
                </p>
                <h3 className="font-semibold text-sm text-[#191B1C]">
                  Mission and Vision of {businessDetails?.name}:
                </h3>
                <p className="font-light text-[10px] text-[#7B878C]">
                  {businessDetails?.mission_and_Vision}
                </p>
                <h2 className="font-semibold text-sm text-[#191B1C]">
                  Our comprehensive range of services includes:
                </h2>
                <ul className="space-y-4">
                  {businessDetails?.serviceFocus?.map((service, index) => (
                    <li key={service.id || index} className="font-light text-[10px] text-[#7B878C]">
                      <span className="font-semibold text-[#636c70]">{service.name}</span> :{' '}
                      {service.percentage} %{' '}
                      <input
                        type="range"
                        value={service.percentage}
                        style={{
                          background: `linear-gradient(to right, black 0%, black ${service.percentage}%, #e5e7eb ${service.percentage}%, #e5e7eb 100%)`,
                        }}
                        className="w-full h-2 rounded-lg appearance-none cursor-pointer"
                        disabled
                      />
                    </li>
                  ))}
                </ul>
              </div>
              <div className="w-full space-y-6 md:w-1/2">
                <div className="p-6 border border-gray-300 rounded-lg">
                  <div className="grid grid-cols-2 pb-4 border-b border-gray-300">
                    <div className="flex flex-col items-center justify-center text-center border-r border-gray-300">
                      <h3 className="font-semibold text-sm text-[#191B1C] mb-2">Project Price</h3>
                      <p className="font-light text-[10px] text-green-600">
                        {businessDetails?.costOfService}
                      </p>
                    </div>
                    <div className="flex flex-col items-center text-center">
                      <SlMap className="mb-2 text-2xl text-gray-700" />
                      <h3 className="font-semibold text-sm text-[#191B1C] mb-2">
                        Company Location
                      </h3>
                      <p className="font-light text-[10px] text-[#7B878C]">
                        {businessDetails?.location}
                      </p>
                    </div>
                  </div>

                  <div className="grid grid-cols-3 gap-6 pt-4">
                    <div className="flex flex-col items-center text-center">
                      <FaUsers className="mb-2 text-2xl text-gray-700" />
                      <h3 className="font-semibold text-sm text-[#191B1C]">Team Size</h3>
                      <p className="font-light text-[10px] text-[#7B878C]">
                        {businessDetails?.team_Size}
                      </p>
                    </div>
                    <div className="flex flex-col items-center text-center">
                      <FaBuilding className="mb-2 text-2xl text-gray-700" />
                      <h3 className="font-semibold text-sm text-[#191B1C]">Organization Type</h3>
                      <p className="font-light text-[10px] text-[#7B878C]">
                        {businessDetails?.legalEntityType}
                      </p>
                    </div>
                    <div className="flex flex-col items-center text-center">
                      <SlHandbag className="mb-2 text-2xl text-gray-700" />
                      <h3 className="font-semibold text-sm text-[#191B1C]">Industry Type</h3>
                      <p className="font-light text-[10px] text-[#7B878C]">
                        {businessDetails?.industryType?.name}
                      </p>
                    </div>
                  </div>
                </div>
                {businessDetails?.representatives.map((reps, index) => (
                  <div
                    key={reps._id}
                    className="flex flex-col p-6 border border-gray-300 rounded-lg">
                    <div className="flex items-center px-4 py-4 border-b border-gray-300">
                      <FaGlobe className="text-[#33363F] text-xl mr-3" />
                      <h3 className="font-semibold text-sm text-[#191B1C]">
                        {businessDetails?.website}
                      </h3>
                    </div>
                    <div className="flex items-center px-4 py-4 border-b border-gray-300">
                      <FaPhoneAlt className="text-[#33363F] text-xl mr-3" />
                      <h3 className="font-semibold text-sm text-[#191B1C]">{reps.phoneNumber}</h3>
                    </div>
                    <div className="flex items-center px-4 py-4">
                      <FaEnvelope className="text-[#33363F] text-xl mr-3" />
                      <h3 className="font-semibold text-sm text-[#191B1C]">{reps.companyEmail}</h3>
                    </div>
                  </div>
                ))}
                <div className="flex flex-col p-6 border border-gray-300 rounded-lg">
                  <div className="p-4">
                    <h3 className="font-semibold text-sm text-[#191B1C] mb-4">Follow Us</h3>
                    <div className="flex gap-4">
                      {/* Slice the array to show only the first 5 social links */}
                      {businessDetails?.sociallink?.slice(0, 5).map((social, index) => {
                        const { platform, url } = social;
                        const lowerPlatform = platform?.toLowerCase();
                        const icon = socialIcons[lowerPlatform] || <FaLink />;
                        // Check if the platform is Twitter
                        const isTwitter = lowerPlatform === 'twitter';

                        return (
                          <a key={social._id} href={url} target="_blank" rel="noopener noreferrer">
                            <div
                              className={`p-3 rounded-lg flex items-center gap-2 ${
                                isTwitter ? 'bg-black text-white' : 'bg-[#CCCCCC] text-white'
                              }`}>
                              <div className="w-4 h-4 text-2xl">{icon}</div>
                            </div>
                          </a>
                        );
                      })}
                      {/* Share profile link button */}
                      <button
                        onClick={shareProfile}
                        className="flex items-center gap-2 bg-[#CCCCCC] p-3 rounded-lg text-white">
                        <FaLink className="" />
                        <span className="text-xs text-white">Share profile link</span>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="p-2 mt-4 bg-white">
            <h2 className="font-semibold text-sm text-[#191B1C]">Payment and purchases</h2>
            <p className="font-light text-[10px] text-[#7B878C] border-b pb-2">All Transactions</p>
            <div className="px-4 overflow-x-auto">
              <table className="w-full mt-4 border-collapse">
                <thead>
                  <tr className="text-left border-b">
                    <th className="p-2 font-semibold text-sm text-[#191B1C]">Description</th>
                    <th className="p-2 font-semibold text-sm text-[#191B1C]">Transaction ID</th>
                    <th className="p-2 font-semibold text-sm text-[#191B1C]">Type</th>
                    <th className="p-2 font-semibold text-sm text-[#191B1C]">Card</th>
                    <th className="p-2 font-semibold text-sm text-[#191B1C]">Date</th>
                    <th className="p-2 font-semibold text-sm text-[#191B1C]">Amount</th>
                    <th className="p-2 font-semibold text-sm text-[#191B1C]">Receipt</th>
                  </tr>
                </thead>
                <tbody>
                  {transactions.map((transaction, index) => (
                    <tr key={transaction.id || index} className="border-b hover:bg-gray-100">
                      <td className="flex items-center p-2">
                        <span className="mr-2">
                          {transaction.amount.startsWith('-') ? (
                            <IoIosArrowRoundUp className="w-4 h-4" />
                          ) : (
                            <IoIosArrowRoundDown className="w-4 h-4" />
                          )}
                        </span>
                        <span className="font-light text-[10px] text-[#7B878C]">
                          {transaction.description}
                        </span>
                      </td>
                      <td className="p-2 font-light text-[10px] text-[#7B878C]">
                        {transaction.id}
                      </td>
                      <td className="p-2 font-light text-[10px] text-[#7B878C]">
                        {transaction.type}
                      </td>
                      <td className="p-2 font-light text-[10px] text-[#7B878C]">
                        {transaction.card}
                      </td>
                      <td className="p-2 font-light text-[10px] text-[#7B878C]">
                        {transaction.date}
                      </td>
                      <td
                        className={`p-2 font-light text-[10px] ${
                          transaction.amount.startsWith('-') ? 'text-red-500' : 'text-green-500'
                        }`}>
                        {transaction.amount}
                      </td>
                      <td className="p-2">
                        <button
                          onClick={() => handlePaymentDownload(transaction)}
                          className="px-4 py-1 border rounded-lg font-light text-[10px] text-[#7B878C]">
                          Download
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="text-right font-light text-[10px] text-[#7B878C] mt-2">See All</p>
          </div>
          <div className="p-2 mt-4 bg-white font-nohemi">
            <h2 className="font-normal text-sm text-[#191B1C]">Quotes</h2>
            <p className="font-light text-[10px] text-[#7B878C] border-b pb-2">sent</p>
            <div className="px-4 overflow-x-auto">
              <table className="w-full mt-4 border-collapse">
                <thead>
                  <tr className="text-left border-b font-nohemi">
                    <th className="p-2 font-semibold text-sm text-[#191B1C]">Company</th>
                    <th className="p-2 font-semibold text-sm text-[#191B1C]">ID</th>
                    <th className="p-2 font-semibold text-sm text-[#191B1C]">Date</th>
                    <th className="p-2 font-semibold text-sm text-[#191B1C]">Phone number</th>
                    <th className="p-2 font-semibold text-sm text-[#191B1C]">Quantity(pcs)</th>
                    <th className="p-2 font-semibold text-sm text-[#191B1C]">Receipt</th>
                  </tr>
                </thead>
                <tbody>
                  {transactions.map((transaction, index) => (
                    <tr key={index} className="border-b font-nohemi hover:bg-gray-100">
                      <td className="p-2 font-light text-[10px] text-[#7B878C]">
                        {transaction.description}
                      </td>
                      <td className="p-2 font-light text-[10px] text-[#7B878C]">
                        {transaction.id}
                      </td>
                      <td className="p-2 font-light text-[10px] text-[#7B878C]">
                        {transaction.date}
                      </td>
                      <td className="p-2 font-light text-[10px] text-[#7B878C]">
                        {transaction.card}
                      </td>
                      <td
                        className={`p-2 font-light text-[10px] ${
                          transaction.amount.startsWith('-') ? 'text-red-500' : 'text-green-500'
                        }`}>
                        {transaction.amount}
                      </td>
                      <td className="p-2">
                        <button
                          onClick={() => handleDownload(transaction)}
                          className="px-4 py-1 border rounded-lg font-light text-[10px] text-[#7B878C]">
                          Download
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="text-right font-light text-[10px] text-[#7B878C] mt-2">See All</p>
          </div>
          <div className="p-2 mt-4 bg-white">
            <div className="w-full bg-white rounded-lg mt-2 h-[270px]">
              <div className="items-start justify-center px-2">
                <p className="font-nohemi text-xs font-light text-[#191B1C] px-2">
                  Company Documents
                </p>
              </div>
              <div className="flex flex-row items-center justify-center gap-6 py-8">
                {businessDetails.documents?.length > 0 ? (
                  businessDetails.documents.map((document, index) => (
                    <button
                      key={document._id}
                      className="w-80 h-32 flex items-center justify-center border-dashed border-2 border-[#CBD5E1] rounded-lg hover:bg-gray-50 cursor-pointer"
                      onClick={() => handleDownloadPdf(document)}>
                      <div className="text-center">
                        <div className="flex items-center justify-center w-10 h-10 mx-auto my-2 rounded-full">
                          <FaRegFilePdf className="w-6 h-6 text-red-500" />
                        </div>
                        <p className="mb-2 text-xs font-semibold font-nohemi">
                          {document.fileName.split('/').pop()}
                        </p>
                        <p className="mb-2 text-xs font-light text-blue-500 font-nohemi">
                          View document
                        </p>
                      </div>
                    </button>
                  ))
                ) : (
                  <p className="text-gray-500">No documents available</p>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CompanyDetailsPage;
